import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock, Pagination } from '../types/general.type'
import { UnitDataMock, UnitIncludedMock } from '../types/unit'

type Props = {
  page: number
  limit: number
  query: string
  sortDir: 'asc' | 'desc'
  id: string
  publicAllEndpoint: string
  /** When false, skip the units list query (e.g. standalone login without Ward/Unit). */
  enabled: boolean
  // default: boolean
}

type UnitsQueryData = {
  data: UnitDataMock[]
  included?: UnitIncludedMock[]
  meta?: { pagination?: Pagination }
}
export const useUnit = (arg: Partial<Props> = {}) => {
  const page = arg?.page || 1
  const limit = arg?.limit || 10
  const query = arg?.query
  const sortDir = arg?.sortDir || 'desc'
  const id = arg?.id
  const publicAllEndpoint = arg?.publicAllEndpoint
  const listEnabled = arg?.enabled ?? true
  // const defaultVal = arg?.default

  const { data, isLoading: unitsIsLoading } = useQuery<UnitsQueryData>({
    enabled: listEnabled,
    queryFn: async (): Promise<UnitsQueryData> => {
      const normalizeEndpoint = (endpoint: string) =>
        endpoint.startsWith('/api/') ? endpoint.replace(/^\/api/, '') : endpoint

      if (publicAllEndpoint) {
        const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnitDataMock[], UnitIncludedMock[]> | UnitDataMock[]>(
          normalizeEndpoint(publicAllEndpoint),
          { skipAuthHeaderPersist: true }
        )
        if (Array.isArray(data)) return { data }
        return data
      }

      let endpoint = `/units?`
      if (page) endpoint = `${endpoint}page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}`
      if (query) endpoint = `${endpoint}&query=${query}`
      if (sortDir) endpoint = `${endpoint}&sort_dir=${sortDir}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnitDataMock[], UnitIncludedMock[]>>(endpoint)
      return data
    },
    queryKey: publicAllEndpoint ? ['units', { publicAllEndpoint }] : ['units', { limit, page, query, sortDir }]
  })

  const { data: singleUnit } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnitDataMock>>(`/units/${id}`)
      return data.data
    },
    queryKey: ['units', id]
  })

  const { mutateAsync: createUnit } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`units`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] })
    }
  })

  const { mutateAsync: updateUnit } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      const { data: updatedData } = await AXIOS_INSTANCE.patch(`units/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return updatedData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] })
    }
  })

  const { mutateAsync: deleteUnit } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`units/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] })
    }
  })

  return {
    createUnit,
    deleteUnit,
    singleUnit,
    unitIncluded: data?.included,
    unitList: data?.data,
    unitPagination: data?.meta?.pagination,
    unitsIsLoading,
    updateUnit
  }
}

export const useSearchUnit = ({ query }: { query?: string }) => {
  const { data } = useQuery({
    queryFn: async () => {
      let endpoint = `/units?`
      if (query) endpoint = `${endpoint}&query=${query}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnitDataMock[], UnitIncludedMock[]>>(endpoint)
      return data
    },
    queryKey: ['units', { query }]
  })
  return {
    unitList: data?.data
  }
}
